// @tennis-matches/services/winterdoppel/trainingsDeskSync

import { generateId } from "@tennis-matches/core/utils";
import { getSupabaseClient } from "../supabase/index.js";
import { createClubPlayerContactFromParsedName } from "../players/index.js";
import { updateClubPerson } from "../masterdata/people.js";
import { createSession } from "../trainerstunden/sessions.js";
import { loadTrainers } from "../trainerstunden/trainers.js";
import { computeTrainerSessionHonorarHourlyRate } from "../trainerstunden/trainer-session-rate.js";
import {
  resolveWinterDoppelParticipantHourlyRate,
  isWinterDoppelPerParticipantPricing,
} from "./groupTrainingCosts.js";
import { getWinterDoppelSeries } from "./series.js";
import { loadWinterDoppelSeriesBilling } from "./billing.js";
import { loadWinterDoppelPlayers } from "./players.js";
import { loadWinterDoppelPlayerGroups } from "./groups.js";
import {
  loadWinterDoppelSessions,
  loadWinterDoppelSessionPlayers,
} from "./sessions.js";
import { updateSession } from "../trainerstunden/sessions.js";
import {
  groupsPlayingTrainingDate,
  trainingGroupSchedulesFromGroups,
} from "./publishedTraining.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

function __timeToMinutes(value) {
  const raw = __asStr(value);
  const m = /^(\d{1,2}):(\d{2})/.exec(raw);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
  return h * 60 + min;
}

export function durationMinutesBetween(startTime, endTime, fallback = 90) {
  const start = __timeToMinutes(startTime);
  const end = __timeToMinutes(endTime);
  if (start == null || end == null || end <= start) {
    return Math.max(15, Number(fallback) || 90);
  }
  return end - start;
}

/**
 * @param {{
 *   group?: { playerIds?: string[] },
 *   sessionId: string,
 *   sessionPlayers?: object[],
 *   playerClubPersonByPlayerId?: Map<string, string>,
 * }} input
 * @returns {string[]}
 */
export function collectGroupDeskPlannedIds({
  group = {},
  sessionId,
  sessionPlayers = [],
  playerClubPersonByPlayerId = new Map(),
  groupsOnSession = 1,
}) {
  const memberIds = new Set(
    (Array.isArray(group.playerIds) ? group.playerIds : [])
      .map((id) => __asStr(id))
      .filter(Boolean),
  );
  const exclusive = Number(groupsOnSession) > 1;
  const sid = __asStr(sessionId);
  const out = [];
  for (const sp of sessionPlayers) {
    if (__asStr(sp?.session_id) !== sid) continue;
    if (__asStr(sp?.role) !== "starter" || __asStr(sp?.rsvp) === "no") continue;
    const playerId = __asStr(sp?.player_id);
    if (exclusive && !memberIds.has(playerId)) continue;
    const clubPersonId = playerClubPersonByPlayerId.get(playerId);
    if (clubPersonId) out.push(clubPersonId);
  }
  return [...new Set(out)];
}

export function resolveWinterDoppelDeskSplitBase(billing) {
  return billing?.charge_planned_participants === false ? "present" : "planned";
}

/**
 * @param {object[]} players
 * @param {{ tenantId?: string }} [opts]
 */
export async function ensureWinterDoppelPlayerClubContacts(
  players = [],
  opts = {},
) {
  const tenantId = __asStr(opts.tenantId);
  const out = [];
  const supabase = await getSupabaseClient();

  for (const player of players) {
    let clubPersonId = __asStr(player?.club_person_id);
    if (!clubPersonId) {
      const created = await createClubPlayerContactFromParsedName(
        player?.display_name,
      );
      if (!created?.ok || !created?.data?.id) {
        throw new Error("winterdoppel.deskSync.contactCreateFailed");
      }
      clubPersonId = __asStr(created.data.id);
      const phone = __asStr(player?.phone);
      if (phone) {
        await updateClubPerson(clubPersonId, { phone });
      }
      const { error } = await supabase
        .from("winterdoppel_players")
        .update({ club_person_id: clubPersonId })
        .eq("id", __asStr(player.id));
      if (error) throw error;
    }
    out.push({ ...player, club_person_id: clubPersonId });
  }
  return out;
}

/**
 * Teilnehmer-Satz für Desk-Session (kein Hallen-Abo).
 * per_participant_hourly → pricing_type hourly_per_participant (kein Split).
 */
export function resolveWinterDoppelDeskSessionRates({
  trainer,
  date,
  groupSize,
  durationMin,
  group = {},
  billing = {},
}) {
  const rateOpts = {
    atDate: date,
    targetGroup: "youth",
    trainingType: "group",
    groupSize: Math.max(1, groupSize),
  };
  const perParticipant = isWinterDoppelPerParticipantPricing(billing);
  const rate = resolveWinterDoppelParticipantHourlyRate({
    group,
    billing,
    trainer,
    atDate: date,
    groupSize: Math.max(1, groupSize),
  });
  const honorarRate = trainer
    ? computeTrainerSessionHonorarHourlyRate(trainer, rateOpts)
    : null;
  return {
    pricing_type: perParticipant ? "hourly_per_participant" : "hourly_split",
    rate_eur_per_hour: rate != null && rate > 0 ? rate : null,
    duration_min: durationMin,
    honorar_type: honorarRate != null ? "per_hour" : null,
    honorar_rate_eur: honorarRate != null ? honorarRate : null,
    honorar_source: honorarRate != null ? "trainer_default" : null,
    honorar_snapshot_at: honorarRate != null ? new Date().toISOString() : null,
  };
}

async function loadExistingDeskLinks(seriesId) {
  const supabase = await getSupabaseClient();
  const { data, error } = await supabase
    .from("winterdoppel_desk_sessions")
    .select(
      "id,winterdoppel_session_id,player_group_id,trainerstunden_session_id",
    )
    .eq("series_id", __asStr(seriesId));
  if (error) throw error;
  const byKey = new Map();
  for (const row of data || []) {
    const key = `${__asStr(row.winterdoppel_session_id)}:${__asStr(row.player_group_id)}`;
    byKey.set(key, row);
  }
  return byKey;
}

/**
 * @param {string} seriesId
 * @returns {Promise<{ created: number, skipped: number }>}
 */
export async function syncWinterDoppelSeriesToTrainingsDesk(
  seriesId,
  { updateExisting = false } = {},
) {
  const series = await getWinterDoppelSeries(seriesId);
  if (!series) throw new Error("winterdoppel.series.notFound");
  if (__asStr(series.series_mode) !== "training") {
    return { created: 0, skipped: 0, ignored: true };
  }

  const billing = (await loadWinterDoppelSeriesBilling(seriesId)) || {};
  const splitBase = resolveWinterDoppelDeskSplitBase(billing);
  const tenantId = __asStr(series.tenant_id);

  let players = await loadWinterDoppelPlayers(seriesId);
  players = await ensureWinterDoppelPlayerClubContacts(players, { tenantId });

  const playerClubPersonByPlayerId = new Map(
    players
      .filter((p) => p?.id && p?.club_person_id)
      .map((p) => [__asStr(p.id), __asStr(p.club_person_id)]),
  );

  const groups = await loadWinterDoppelPlayerGroups(seriesId);
  const groupSchedules = trainingGroupSchedulesFromGroups(
    groups,
    series.season_end,
  );
  const sessions = (await loadWinterDoppelSessions(seriesId)).filter(
    (s) => s?.status !== "cancelled",
  );
  const sessionPlayers = await loadWinterDoppelSessionPlayers(
    sessions.map((s) => s.id),
  );

  const trainersRes = await loadTrainers();
  const trainers = trainersRes?.ok ? trainersRes.data || [] : [];
  const trainersById = new Map(
    trainers.map((tr) => [__asStr(tr.id), tr]).filter(([id]) => id),
  );

  const existingLinks = await loadExistingDeskLinks(seriesId);
  const supabase = await getSupabaseClient();

  let created = 0;
  let skipped = 0;
  let updated = 0;

  for (const session of sessions) {
    const sessionId = __asStr(session.id);
    const sessionDate = __asStr(session.session_date).slice(0, 10);
    if (!sessionId || !sessionDate) continue;
    const playing = groupsPlayingTrainingDate(groupSchedules, sessionDate);
    const playingIds = new Set(
      playing.map((gs) => __asStr(gs.group?.id)).filter(Boolean),
    );
    const groupsOnSession = playing.length;

    for (const group of groups) {
      const groupId = __asStr(group?.id);
      if (!groupId) continue;
      const playsDate = playingIds.has(groupId);
      const linkKey = `${sessionId}:${groupId}`;
      const existing = existingLinks.get(linkKey);
      if (!playsDate && !existing) continue;

      const plannedIds = collectGroupDeskPlannedIds({
        group,
        sessionId,
        sessionPlayers,
        playerClubPersonByPlayerId,
        groupsOnSession,
      });

      if (existing) {
        if (updateExisting) {
          const deskId = __asStr(existing.trainerstunden_session_id);
          if (deskId) {
            const result = await updateSession(deskId, {
              date: sessionDate,
              planned_ids: plannedIds,
              group_size: plannedIds.length,
            });
            if (!result?.ok) {
              throw new Error("winterdoppel.deskSync.sessionCreateFailed");
            }
            updated += 1;
          }
        } else {
          skipped += 1;
        }
        continue;
      }

      if (plannedIds.length < 2) continue;

      const trainerId = __asStr(group.trainer_id);
      const trainer = trainerId ? trainersById.get(trainerId) : null;
      const startTime =
        __asStr(group.start_time).slice(0, 5) ||
        __asStr(series.start_time).slice(0, 5) ||
        "18:00";
      const endTime =
        __asStr(group.end_time).slice(0, 5) ||
        __asStr(series.end_time).slice(0, 5) ||
        "";
      const durationMin = durationMinutesBetween(startTime, endTime, 90);
      const rates = resolveWinterDoppelDeskSessionRates({
        trainer,
        date: sessionDate,
        groupSize: plannedIds.length,
        durationMin,
        group,
        billing,
      });

      const deskPayload = {
        id: generateId(12),
        date: sessionDate,
        start_time: `${startTime}:00`.slice(0, 8),
        trainer_id: trainerId || null,
        location_id: __asStr(series.location_id) || null,
        title: `${__asStr(series.name)} – ${__asStr(group.name)}`,
        notes: `Regeltermine:${__asStr(series.id)}`,
        target_group: "youth",
        training_type: "group",
        group_size: plannedIds.length,
        split_base: splitBase,
        planned_ids: plannedIds,
        present_ids: [],
        attendance_confirmed: false,
        recurrence: "once",
        ...(tenantId ? { tenant_id: tenantId } : {}),
        ...rates,
      };

      const result = await createSession(deskPayload);
      if (!result?.ok || !result?.data?.id) {
        throw new Error("winterdoppel.deskSync.sessionCreateFailed");
      }

      const linkRow = {
        series_id: __asStr(seriesId),
        winterdoppel_session_id: sessionId,
        player_group_id: groupId,
        trainerstunden_session_id: __asStr(result.data.id),
        ...(tenantId ? { tenant_id: tenantId } : {}),
      };
      const { error: linkErr } = await supabase
        .from("winterdoppel_desk_sessions")
        .insert(linkRow);
      if (linkErr) throw linkErr;

      existingLinks.set(linkKey, linkRow);
      created += 1;
    }
  }

  return { created, skipped, updated, ignored: false };
}
